import { Elements } from '@kontent-ai/delivery-sdk';
import React, { useState, useEffect } from 'react';
import RichTextComponents from './RichTextComponents';
import DOMPurify from 'dompurify';
import InteractiveSingleVideo from './InteractiveSingleVideo';
import InteractiveBranching from './InteractiveBranching';
import CustomModalFrameless from './CustomModalFrameless';
import ComponentTitle from './ComponentTitle';
import VideoJSIFrame from './VideoJsIFrame';

const FramelessDigitalAsset: React.FC<{ element: any }> = ({ element }) => {
  const [show, setShow] = useState(false);
  const handleClose = (): void => setShow(false);
  const [modalPosition, setModalPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);
  const [selectedImage, setSelectedImage] = useState<{
    url: string;
    description: string;
    digitalAssetID: string;
    permissionLang: Elements.RichTextElement;
  } | null>(null);

  // Store processed header and footer data in state to avoid recalculation
  const [headerProcessed, setHeaderProcessed] = useState<{
    data: any;
    type: string | null;
  }>({ data: null, type: null });

  const [footerProcessed, setFooterProcessed] = useState<{
    data: any;
    type: string | null;
  }>({ data: null, type: null });

  // Process header and footer data once on component mount
  useEffect(() => {
    // Process header data
    const headerItem = element.header_image?.linkedItems[0];
    if (headerItem) {
      setHeaderProcessed({
        data: headerItem.elements,
        type: headerItem.system?.type,
      });
    }

    // Process footer data (separately)
    const footerItem = element.footer_image?.linkedItems[0];
    if (footerItem) {
      setFooterProcessed({
        data: footerItem.elements,
        type: footerItem.system?.type,
      });
    }
  }, [element]);

  const updateSelectedImage = (
    image: {
      url: string;
      description: string;
      title: string;
      digitalAssetID: string;
      permissionLang: Elements.RichTextElement;
    } | null,
  ): void => {
    setSelectedImage(image);
  };

  // Utility function to check if rich text contains meaningful content
  const hasMeaningfulText = (richText: any): boolean => {
    if (!richText?.value) return false;
    const strippedText = DOMPurify.sanitize(richText.value, {
      ALLOWED_TAGS: [],
      ALLOWED_ATTR: [],
    }).trim();
    return strippedText.length > 0;
  };

  const renderHeaderContent = (): React.ReactNode => {
    const { data: headerData, type: headerType } = headerProcessed;

    if (!headerData) {
      return null;
    }

    // Handle different interactive video types
    if (
      headerType === 'interactive_branching___video' &&
      headerData.brightcove_project_id?.value
    ) {
      return (
        <>
          <div className="toolbox-panel-body embed-responsive embed-responsive-16by9">
            <InteractiveSingleVideo element={headerData} />
          </div>
          <div className="da-footer" style={{ marginTop: '0' }}>
            {hasMeaningfulText(headerData.permission_language) && (
              <span className="daid">
                <RichTextComponents element={headerData.permission_language} />
              </span>
            )}
            {headerData.digital_asset_id?.value && (
              <span className="daid">
                [{headerData.digital_asset_id?.value}]
              </span>
            )}
          </div>
        </>
      );
    }

    if (
      headerType === 'interactive_branching___multiple_videos' &&
      headerData.brightcove_experience_id?.value
    ) {
      return (
        <>
          <div className="toolbox-panel-body embed-responsive embed-responsive-16by9">
            <InteractiveBranching element={headerData} />
          </div>
          <div className="da-footer" style={{ marginTop: '0' }}>
            {hasMeaningfulText(headerData.permission_language) && (
              <span className="daid">
                <RichTextComponents element={headerData.permission_language} />
              </span>
            )}
            {headerData.digital_asset_id?.value && (
              <span className="daid">
                [{headerData.digital_asset_id?.value}]
              </span>
            )}
          </div>
        </>
      );
    }

    if (
      headerData.brightcove_account_id?.value &&
      !headerData.brightcove_project_id?.value &&
      !headerData.brightcove_experience_id?.value
    ) {
      const altText =
        headerData.description___alt_text?.value || 'Video content';

      const videoJsOptions = {
        autoplay: true,
        controls: true,
        responsive: true,
        fluid: true,
        sources: [
          {
            src: `https://players.brightcove.net/${headerData.brightcove_account_id.value}/${headerData.brightcove_player_id.value}_default/index.html?videoId=${headerData.brightcove_video_id.value}`,
            type: 'application/vnd.apple.mpegurl',
          },
        ],
      };

      return (
        <>
          <div className="toolbox-panel-body embed-responsive embed-responsive-16by9">
            <VideoJSIFrame options={videoJsOptions} altText={altText} />
          </div>
          <div className="da-footer" style={{ marginTop: '0' }}>
            <span className="daid" style={{ textAlign: 'left' }}>
              {hasMeaningfulText(headerData.permission_language) &&
                DOMPurify.sanitize(headerData.permission_language?.value, {
                  ALLOWED_TAGS: [],
                  ALLOWED_ATTR: [],
                }).trim()}
              {headerData.digital_asset_id?.value &&
                ` [${headerData.digital_asset_id?.value}]`}
            </span>
          </div>
        </>
      );
    }

    if (headerData.image_asset?.value?.[0]?.url) {
      return (
        <div>
          <div className="equation">
            <img
              src={headerData.image_asset.value[0].url}
              alt={
                headerData.image_asset.value[0].description || 'Header Image'
              }
              className="img-responsive center-block"
              onClick={(event: React.MouseEvent<HTMLImageElement>): void => {
                setShow(true);
                setModalPosition({
                  top: event.clientY,
                  left: event.clientX,
                });
                updateSelectedImage({
                  url: headerData.image_asset.value[0].url,
                  description:
                    headerData.image_asset.value[0].description || '',
                  title: headerData.title?.value || '',
                  digitalAssetID: headerData.digital_asset_id?.value || '',
                  permissionLang: headerData.image_permission_language || null,
                });
              }}
            />
          </div>
          {/* TODO: Add permission language and digital asset ID display for header images
          <div className="da-footer" style={{ marginTop: '0' }}>
            {hasMeaningfulText(headerData.permission_language) && (
              <span className="daid">
                <RichTextComponents element={headerData.permission_language} />
              </span>
            )}
            {headerData.digital_asset_id?.value && (
              <span className="daid">
                [{headerData.digital_asset_id?.value}]
              </span>
            )}
          </div>
          */}
        </div>
      );
    }

    return null;
  };

  const renderFooterContent = (): React.ReactNode => {
    const { data: footerData, type: footerType } = footerProcessed;

    if (!footerData) {
      return null;
    }

    // Handle different interactive video types
    if (
      footerType === 'interactive_branching___video' &&
      footerData.brightcove_project_id?.value
    ) {
      return (
        <>
          <div className="toolbox-panel-body embed-responsive embed-responsive-16by9">
            <InteractiveSingleVideo element={footerData} />
          </div>
          <div className="da-footer" style={{ marginTop: '0' }}>
            {hasMeaningfulText(footerData.permission_language) && (
              <span className="daid">
                <RichTextComponents element={footerData.permission_language} />
              </span>
            )}
            {footerData.digital_asset_id?.value && (
              <span className="daid">
                [{footerData.digital_asset_id?.value}]
              </span>
            )}
          </div>
        </>
      );
    }

    if (
      footerType === 'interactive_branching___multiple_videos' &&
      footerData.brightcove_experience_id?.value
    ) {
      return (
        <>
          <div className="toolbox-panel-body embed-responsive embed-responsive-16by9">
            <InteractiveBranching element={footerData} />
          </div>
          <div className="da-footer" style={{ marginTop: '0' }}>
            {hasMeaningfulText(footerData.permission_language) && (
              <span className="daid">
                <RichTextComponents element={footerData.permission_language} />
              </span>
            )}
            {footerData.digital_asset_id?.value && (
              <span className="daid">
                [{footerData.digital_asset_id?.value}]
              </span>
            )}
          </div>
        </>
      );
    }

    if (
      footerData.brightcove_account_id?.value &&
      !footerData.brightcove_project_id?.value &&
      !footerData.brightcove_experience_id?.value
    ) {
      const altText =
        footerData.description___alt_text?.value || 'Video content';

      const videoJsOptions = {
        autoplay: true,
        controls: true,
        responsive: true,
        fluid: true,
        sources: [
          {
            src: `https://players.brightcove.net/${footerData.brightcove_account_id.value}/${footerData.brightcove_player_id.value}_default/index.html?videoId=${footerData.brightcove_video_id.value}`,
            type: 'application/vnd.apple.mpegurl',
          },
        ],
      };

      return (
        <>
          <div className="toolbox-panel-body embed-responsive embed-responsive-16by9">
            <VideoJSIFrame options={videoJsOptions} altText={altText} />
          </div>
          <div className="da-footer" style={{ marginTop: '0' }}>
            <span className="daid" style={{ textAlign: 'left' }}>
              {hasMeaningfulText(footerData.permission_language) &&
                DOMPurify.sanitize(footerData.permission_language?.value, {
                  ALLOWED_TAGS: [],
                  ALLOWED_ATTR: [],
                }).trim()}
              {footerData.digital_asset_id?.value &&
                ` [${footerData.digital_asset_id?.value}]`}
            </span>
          </div>
        </>
      );
    }

    if (footerData.image_asset?.value?.[0]?.url) {
      return (
        <div>
          <div className="equation">
            <img
              src={footerData.image_asset.value[0].url}
              alt={
                footerData.image_asset.value[0].description || 'Footer Image'
              }
              className="img-responsive center-block"
              onClick={(event: React.MouseEvent<HTMLImageElement>): void => {
                setShow(true);
                setModalPosition({
                  top: event.clientY,
                  left: event.clientX,
                });
                updateSelectedImage({
                  url: footerData.image_asset.value[0].url,
                  description:
                    footerData.image_asset.value[0].description || '',
                  title: footerData.title?.value || '',
                  digitalAssetID: footerData.digital_asset_id?.value || '',
                  permissionLang: footerData.image_permission_language || null,
                });
              }}
            />
          </div>
          {/* TODO: Add permission language and digital asset ID display for footer images
          <div className="da-footer" style={{ marginTop: '0' }}>
            {hasMeaningfulText(footerData.permission_language) && (
              <span className="daid">
                <RichTextComponents element={footerData.permission_language} />
              </span>
            )}
            {footerData.digital_asset_id?.value && (
              <span className="daid">
                [{footerData.digital_asset_id?.value}]
              </span>
            )}
          </div>
          */}
        </div>
      );
    }

    return null;
  };

  const renderBodyContent = (): React.ReactNode => {
    if (!hasMeaningfulText(element?.body)) {
      return null;
    }

    return (
      <blockquote>
        <RichTextComponents element={element?.body} />
      </blockquote>
    );
  };

  return (
    <>
      {show && selectedImage && (
        <CustomModalFrameless
          show={true}
          onHide={handleClose}
          element={element}
          selectedImage={selectedImage}
          position={modalPosition}
        />
      )}
      <div>
        {element.title && <ComponentTitle title={element.title.value} />}
        {/* Header Content */}
        <div className="header-content">{renderHeaderContent()}</div>

        {/* Body Content */}
        <div className="body-content">{renderBodyContent()}</div>

        {/* Footer Content */}
        <div
          className="footer-content"
          data-content-type={footerProcessed.type}
        >
          {renderFooterContent()}
        </div>
      </div>
    </>
  );
};

export default FramelessDigitalAsset;
